import { z } from "zod";
// Identificativi e testi liberi dei corpi JSON. PostgreSQL rifiuta un parametro di testo con il byte 0x00
// (SQLSTATE 22021) e jsonb rifiuta `\u0000` (SQLSTATE 22P05) e la metà di una coppia di surrogati senza l'altra
// (SQLSTATE 22P02): un NUL o una metà sola che arrivasse a una query farebbe rispondere `500 internal_error`. Questi
// schemi li fermano prima, e il server risponde `422 validation_failed` con il nome del campo.
/**
 * Identificativo di una riga: i nostri vengono da `crypto.randomUUID()`, quelli di Better Auth hanno un formato
 * loro. Un alfabeto stretto (lettere, cifre, `-` e `_`) e una lunghezza limitata, senza presumere UUID; il server lo
 * usa anche per gli id nel percorso e negli header (`apps/server/src/path-params.ts`).
 */
export const ID_PATTERN = /^[A-Za-z0-9_-]{1,128}$/;
export const idSchema = z.string().regex(ID_PATTERN);
// Caratteri di controllo Unicode (categoria Cc: U+0000–U+001F, U+007F, U+0080–U+009F) e caratteri che girano il verso
// del testo (proprietà Bidi_Control: U+061C, U+200E, U+200F, U+202A–U+202E, U+2066–U+2069). Decisione del 1° ottobre
// 2026 in `docs/DECISIONS.md` («i caratteri dei testi scritti a mano»): in un testo su una riga tab e a capo diventano
// uno spazio, perché arrivano quando si incolla un nome da un foglio o da un'email; gli altri caratteri di controllo,
// quelli che girano il verso e gli spazi invisibili, che possono camuffare un nome, si rifiutano. Un testo su più righe tiene tab e a capo e
// rifiuta gli altri caratteri di controllo. Il controllo viene prima del trim, che altrimenti toglierebbe in silenzio
// un carattere di controllo ai bordi; il trim toglie poi gli spazi.
const CONTROL_CHARACTER = /\p{Cc}/u;
const CONTROL_CHARACTER_EXCEPT_LINE_BREAKS = /[^\P{Cc}\t\n\r]/u;
// Caratteri invisibili che rendono uguali due nomi diversi: quelli che Unicode dichiara ignorabili
// (Default_Ignorable_Code_Point: trattino morbido, spazio di larghezza zero, word joiner, BOM, riempitivi Hangul e
// simili). Restano i soli che servono davvero, perché compongono le emoji e alcune scritture: ZWNJ e ZWJ, selettori
// di variante (U+FE00–U+FE0F, U+E0100–U+E01EF) e tag delle bandiere (U+E0020–U+E007F).
const CONTROL_OR_BIDI = /[\p{Cc}\p{Bidi_C}]/u;
const IGNORABLE = /\p{Default_Ignorable_Code_Point}/gu;
const keptIgnorable = (codePoint) => codePoint === 0x200c ||
    codePoint === 0x200d ||
    (codePoint >= 0xfe00 && codePoint <= 0xfe0f) ||
    (codePoint >= 0xe0020 && codePoint <= 0xe007f) ||
    (codePoint >= 0xe0100 && codePoint <= 0xe01ef);
/** Se un testo su una riga contiene un carattere da rifiutare: di controllo, che gira il verso o invisibile. */
function hasForbiddenSingleLineCharacter(value) {
    if (CONTROL_OR_BIDI.test(value))
        return true;
    for (const [character] of value.matchAll(IGNORABLE))
        if (!keptIgnorable(character.codePointAt(0) ?? 0))
            return true;
    return false;
}
// Tab e a capo in ogni forma: LF, VT, FF, CR, NEL, separatore di riga e di paragrafo.
const LINE_BREAKS = /[\t\n\v\f\r\u0085\u2028\u2029]+/;
const SPACE = /\p{Zs}/u;
/**
 * Tab e a capo, con gli spazi che li toccano (anche quelli non separabili di un testo copiato da una pagina o da
 * un'email), diventano uno spazio solo. Senza espressioni con spazi ripetuti prima di un a capo, che su un corpo di
 * 64 KB di spazi costerebbero secondi: si divide sugli a capo e si tolgono gli spazi ai bordi di ogni pezzo con un
 * ciclo. Gli altri caratteri restano dove sono, perché il controllo che segue li rifiuti.
 */
function joinLines(value) {
    if (!LINE_BREAKS.test(value))
        return value;
    return value.split(LINE_BREAKS).map(stripSpaces).filter((part) => part !== "").join(" ");
}
function stripSpaces(part) {
    let start = 0;
    let end = part.length;
    while (start < end && SPACE.test(part[start] ?? ""))
        start += 1;
    while (end > start && SPACE.test(part[end - 1] ?? ""))
        end -= 1;
    return part.slice(start, end);
}
/** Se il testo contiene un carattere di controllo, compresi tab e a capo. */
export function hasControlCharacter(value) {
    return CONTROL_CHARACTER.test(value);
}
// La metà di una coppia di surrogati UTF-16 senza l'altra (U+D800–U+DFFF fuori da una coppia), per esempio un'emoji
// tagliata a metà da un client: non è un carattere. In un campo jsonb PostgreSQL la rifiuta (un `500`), in un campo di
// testo la salva come «�». Con il flag `u` una coppia intera è un carattere solo, quindi `\p{Cs}` trova soltanto le
// metà sole. Si rifiuta in ogni testo (decisione del 1° ottobre 2026 in `docs/DECISIONS.md`, «i caratteri dei testi
// scritti a mano»).
const LONE_SURROGATE = /\p{Cs}/u;
const LONE_SURROGATE_MESSAGE = "Unpaired UTF-16 surrogates are not allowed";
/** Se il testo contiene la metà di una coppia di surrogati UTF-16 senza l'altra. */
export function hasLoneSurrogate(value) {
    return LONE_SURROGATE.test(value);
}
/**
 * Testo su una riga (nomi, codici): tab e a capo diventano uno spazio, gli altri caratteri di controllo, quelli che
 * girano il verso, gli spazi invisibili e le metà sole di una coppia di surrogati si rifiutano; poi senza spazi ai
 * bordi, da 1 a `max` caratteri (da 0 con `allowEmpty`).
 */
export function singleLineTextSchema(max, options = {}) {
    const text = z
        .string()
        .refine((value) => !hasLoneSurrogate(value), { message: LONE_SURROGATE_MESSAGE })
        .overwrite(joinLines)
        .refine((value) => !hasForbiddenSingleLineCharacter(value), { message: "Control, bidirectional and invisible characters are not allowed" })
        .trim();
    return (options.allowEmpty === true ? text : text.min(1)).max(max);
}
/**
 * Testo su più righe (descrizioni, note) e testo libero che non è un nome: tab e a capo restano, gli altri caratteri di
 * controllo e le metà sole di una coppia di surrogati si rifiutano; poi senza spazi ai bordi, da 1 a `max` caratteri
 * (da 0 con `allowEmpty`).
 */
export function multiLineTextSchema(max, options = {}) {
    const text = z
        .string()
        .refine((value) => !hasLoneSurrogate(value), { message: LONE_SURROGATE_MESSAGE })
        .refine((value) => !CONTROL_CHARACTER_EXCEPT_LINE_BREAKS.test(value), { message: "Control characters other than line breaks and tabs are not allowed" })
        .trim();
    return (options.allowEmpty === true ? text : text.min(1)).max(max);
}
// Le stesse espressioni, per togliere invece di rifiutare.
const LONE_SURROGATES = new RegExp(LONE_SURROGATE.source, "gu");
const CONTROL_OR_BIDI_CHARACTERS = new RegExp(CONTROL_OR_BIDI.source, "gu");
const CONTROL_CHARACTERS_EXCEPT_LINE_BREAKS = new RegExp(CONTROL_CHARACTER_EXCEPT_LINE_BREAKS.source, "gu");
/** Il testo con ogni metà sola di una coppia di surrogati UTF-16 sostituita da «�» (U+FFFD), come fa `toWellFormed`. */
export function replaceLoneSurrogates(value) {
    return hasLoneSurrogate(value) ? value.replace(LONE_SURROGATES, "\uFFFD") : value;
}
// I testi che nessuno ha scritto a mano, per esempio quelli importati da Close, non si possono rifiutare con l'errore
// sul campo: si rendono accettabili con le stesse regole (questione aperta n. 130). La metà sola di una coppia di
// surrogati diventa «�», così il testo resta dov'era e si vede che lì c'era un carattere rotto; i caratteri che lo
// schema rifiuta si tolgono. Il testo che ne esce lo schema lo accetta, salvo gli spazi ai bordi, che toglie chi chiama
// (o il trim dello schema), e la lunghezza.
/**
 * Un testo su una riga reso come `singleLineTextSchema` lo accetta: ogni metà sola di una coppia di surrogati diventa
 * «�», tab e a capo con gli spazi che li toccano diventano uno spazio come nello schema, e i caratteri di controllo,
 * quelli che girano il verso e gli spazi invisibili si tolgono (restano quelli che compongono emoji e scritture).
 */
export function acceptableSingleLineText(value) {
    return joinLines(replaceLoneSurrogates(value))
        .replace(CONTROL_OR_BIDI_CHARACTERS, "")
        .replace(IGNORABLE, (character) => (keptIgnorable(character.codePointAt(0) ?? 0) ? character : ""));
}
/**
 * Un testo su più righe reso come `multiLineTextSchema` lo accetta: ogni metà sola di una coppia di surrogati diventa
 * «�» e i caratteri di controllo si tolgono, tranne tab e a capo; quelli invisibili o che girano il verso restano,
 * perché lo schema li accetta.
 */
export function acceptableMultiLineText(value) {
    return replaceLoneSurrogates(value).replace(CONTROL_CHARACTERS_EXCEPT_LINE_BREAKS, "");
}
//# sourceMappingURL=text.js.map